import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { InstantSearch, SearchBox } from 'react-instantsearch';
import { searchClient, BASE_INDEX } from './algolia';
import { BasketContext } from './context-basket';
import * as Basket from './page-basket';
import { ScanButton } from './scanner/scan-button';

import preloaderStampede from 'url:./assets/preloader-stampede.gif';
import preloaderStampedeShort from 'url:./assets/preloader-stampede-short.gif';
import iconBasket from 'url:./assets/icon-basket.svg';
import { showConsentSettings } from './consent';

// Shared formatter for on-screen counts (facet counts, chip counts, member
// counts, etc.) so the Greek dot thousands-separator convention (e.g.
// "4.960") is applied consistently everywhere instead of each file rolling
// its own `Intl.NumberFormat('el-EL')` instance. Prices have their own
// formatter -- see `priceFormat`/`formatPrice` in product.tsx.
export const countFormat = new Intl.NumberFormat('el-EL');

export function Logo({ size = 'xl' }) {
  return (
    <h1
      className={
        'text-' + size + ' py-1 font-extrabold tracking-wide text-lime-600'
      }
    >
      <Link to="/">zuper</Link>
    </h1>
  );
}

export function Footer() {
  return (
    <div className="text-sm text-center text-gray-500">
      <p className="pb-8">
        &copy; 2023-2026{' '}
        <a
          href="https://www.pixelated-noise.com"
          target="_blank"
          className="underline"
        >
          Pixelated Noise Ltd.
        </a>{' '}
        -{' '}
        <Link to="/faq">
          <span className="underline text-gray-900">Συχνές ερωτήσεις</span>
        </Link>{' '}
        -{' '}
        <a href="/blog/">
          <span className="underline text-gray-900">Blog</span>
        </a>{' '}
        -{' '}
        <Link to="/privacy">
          <span className="underline text-gray-900">Απόρρητο</span>
        </Link>{' '}
        -{' '}
        <button type="button" onClick={showConsentSettings}>
          <span className="underline text-gray-900">Ρυθμίσεις cookies</span>
        </button>
      </p>
      <p className="pb-4">
        Για οποιοδήποτε σχόλιο, email στο{' '}
        <a href="mailto:feedback@zuper-market.gr" className="underline">
          feedback@zuper-market.gr
        </a>
      </p>
    </div>
  );
}

export function HorizontalPreload({ short = false }) {
  return short ? (
    <img className="opacity-40" src={preloaderStampedeShort} />
  ) : (
    <img className="opacity-40" src={preloaderStampede} />
  );
}

// Shared with any element that should look like a PillButton but isn't one
// (e.g. CategoryNav's chips, which are react-router `Link`s so crawlers can
// follow them, not click handlers on a `div`).
export function pillButtonClassName({
  isSelected = false,
  variant = 'default',
}) {
  const baseClasses =
    'flex items-center text-nowrap select-none cursor-pointer justify-center text-sm rounded-full px-4 h-8';
  const variantClasses =
    variant === 'grey'
      ? 'bg-gray-300 hover:bg-gray-400 text-black'
      : 'bg-ui hover:bg-green-500 text-white';
  const selectedClasses = isSelected ? ' border border-gray-700' : '';

  return baseClasses + ' ' + variantClasses + selectedClasses;
}

export function PillButton({
  children,
  onClick,
  isSelected = false,
  variant = 'default',
  ...props
}) {
  return (
    <div
      className={pillButtonClassName({ isSelected, variant })}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

function BasketButton({ basket }) {
  const itemCount = basket.products
    ? Basket.sumItemCount(Object.values(basket.products))
    : 0;

  return (
    <Link to="/basket">
      <div className="bg-ui hover:bg-green-500 rounded-xl text-white w-[5.5rem]">
        <div className="flex items-center justify-center">
          <img src={iconBasket} className="w-10 h-10 p-1 mr-1" />
          <div className="w-7">{itemCount}</div>
        </div>
      </div>
    </Link>
  );
}

function HeaderSearchBox() {
  const navigate = useNavigate();

  return (
    <div className="relative">
      <SearchBox
        placeholder="Αναζήτηση προϊόντων..."
        searchAsYouType={false}
        autoFocus={false}
        classNames={{
          input:
            'rounded-full w-full border-gray-300 focus:border-lime-600 pl-10 h-10',
          submit: 'hidden',
          reset: 'hidden',
          loadingIndicator: 'hidden',
        }}
        onSubmit={(event) => {
          event.preventDefault();
          const query = event.currentTarget.querySelector('input').value;
          if (query.trim()) {
            navigate(
              `/search?${encodeURIComponent(BASE_INDEX)}%5Bquery%5D=${encodeURIComponent(query)}`
            );
          }
        }}
      />
    </div>
  );
}

export function AppHeader({ maxWidth = 'max-w-3xl' }) {
  const basket = useContext(BasketContext);

  return (
    <InstantSearch searchClient={searchClient} indexName={BASE_INDEX}>
      {/* Mobile header */}
      <div className="lg:hidden text-center sticky top-0 z-40 shadow-md bg-white px-2 pt-1 pb-2">
        <div className="flex">
          <div className="w-32"></div>
          <div className="grow">
            <Logo size="2xl" />
          </div>
          <div className="w-32 flex justify-end">
            <BasketButton basket={basket} />
          </div>
        </div>
        <div className="pt-2 flex items-center gap-2">
          <ScanButton variant="compact" />
          <div className="flex-1">
            <HeaderSearchBox />
          </div>
        </div>
      </div>

      {/* Desktop header */}
      <div className={`hidden lg:block mb-6 ${maxWidth} mx-auto px-8`}>
        <div className="flex space-x-2">
          <div className="grow">
            <HeaderSearchBox />
          </div>
          <BasketButton basket={basket} />
        </div>
      </div>
    </InstantSearch>
  );
}
